/* eslint-disable no-unused-vars */
import React, { Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { ThemeProvider, makeStyles } from '@material-ui/core/styles';
import routes from '../routes';
import { backgroundColors, themePrimary } from '../material-ui-theme';
import { auth } from '../api';
import BrandDetails from '../partials/Authentication/BrandDetails';
import CustomAsideInfo from '../partials/Authentication/CustomAsideInfo';
import { useSelector } from 'react-redux';
import { getLayoutLoginMarginTop } from '../stores/local-settings/getters';

export const authPagesAdjustments = [
  {
    name: 'pre-login-consent',
    quantiplyLoginContainerMaxWidth: '570px',
  },
];

const getActiveAuthPage = () => {
  const pageName = window.location.pathname.split('/').filter(Boolean).pop();

  return authPagesAdjustments.find((page) => page.name === pageName);
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100vw',
    height: '100vh',
    background: backgroundColors.grey,
    display: 'flex',

    '& .formContainer': {
      paddingTop: 22,
      paddingBottom: 22,
    },
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    overflow: 'auto',
    '& > .MuiGrid-root': {
      height: '100%',
    },
  },
  centerContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'initial',
    },
  },
  QuantiplyLoginContainer: {
    order: 1,
    maxWidth: 'var(--QuantiplyLoginContainerMaxWidth)',
    display: 'flex',
    alignItems: 'center',
    marginTop: 30,

    [theme.breakpoints.up('md')]: {
      order: 2,
      marginTop: 'var(--marginTop)',
    },
  },
}));

function QuantiplyLoginContainer({ getRoutes }) {
  const classes = useStyles();
  const j = useSelector(getLayoutLoginMarginTop) || 30;

  return (
    <div
      className={classes.QuantiplyLoginContainer}
      style={{
        '--marginTop': `${j}px`,
        '--QuantiplyLoginContainerMaxWidth': `${
          getActiveAuthPage()?.quantiplyLoginContainerMaxWidth || '406px'
        }`,
      }}
    >
      <Grid container spacing={2}>
        <BrandDetails />
        <Suspense fallback={<span />}>
          <Switch>{getRoutes(routes)}</Switch>
        </Suspense>
      </Grid>
    </div>
  );
}

function Authenticate({ getRoutes }) {
  const classes = useStyles();

  return (
    <div className="authenticate">
      <ThemeProvider theme={themePrimary}>
        <div className={classes.root}>
          <main className={classes.content}>
            <Grid container spacing={2}>
              <Grid item xs={12} className={classes.centerContainer}>
                <QuantiplyLoginContainer getRoutes={getRoutes} />
                <CustomAsideInfo />
              </Grid>
            </Grid>
          </main>
        </div>
      </ThemeProvider>
    </div>
  );
}

class AuthenticateBase extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      localState: {
        mobile: null,
        token: null,
      },
      errors: {},
    };

    this.updateLocalState = this.updateLocalState.bind(this);
    this.authPreRegister = this.authPreRegister.bind(this);
    this.authRegister = this.authRegister.bind(this);
    this.authPostRegister = this.authPostRegister.bind(this);
    this.authLogin = this.authLogin.bind(this);
    this.authResendOtp = this.authResendOtp.bind(this);
    this.handleErrors = this.handleErrors.bind(this);
  }

  handleErrors(error) {
    let errors = {};
    if (
      error &&
      error.response &&
      error.response.data &&
      error.response.data.errors
    ) {
      const { errors } = error.response.data;

      if (Object.keys(errors).length < 1) {
        errors.global = error.response.data.message;
      }

      this.setState({ errors });
    } else if (error.message) {
      errors.global = error.message;
    }

    if (Object.keys(errors).length) {
      this.setState({ errors });
    }
  }

  // Auth API
  authPreRegister(data) {
    this.updateLocalState(data);

    return auth.preRegister(data);
  }

  authRegister(data) {
    const dataExtended = Object.assign({}, this.state.localState, data);
    return auth.register(dataExtended).then((res) => {
      if (
        res &&
        res.data &&
        res.data.message &&
        res.data.data &&
        res.data.data.token
      ) {
        this.updateLocalState({
          token: res.data.data.token,
        });
      }

      return res;
    });
  }

  authPostRegister(data) {
    const dataExtended = Object.assign(
      {},
      { token: this.state.localState.token },
      data
    );
    return auth.postRegister(dataExtended).then((res) => {
      this.setState({
        localState: {
          mobile: null,
          token: null,
        },
      });

      return res;
    });
  }

  authLogin(data) {
    return auth.login(data);
  }

  authResendOtp() {
    const data = { token: this.state.localState.token };
    return auth.resentOtp(data);
  }

  authForgetPassword(data) {
    return auth.forgetPassword(data);
  }

  authForgetPasswordSms(data) {
    return auth.forgetPasswordSms(data);
  }

  authResetTokenCheck(data) {
    return auth.resetTokenCheck(data);
  }

  authResetPassword(data) {
    return auth.resetPassword(data);
  }

  updateLocalState(newValues) {
    const { localState } = this.state;
    const newLocalState = Object.assign({}, localState, newValues);

    this.setState({
      localState: newLocalState,
    });
  }

  getRoutes = () => {
    return routes.map((props) => (
      <Route
        key={props.id}
        path={props.path}
        render={(p) => (
          <props.component
            {...p}
            localState={this.state.localState}
            authPreRegister={this.authPreRegister}
            authRegister={this.authRegister}
            authPostRegister={this.authPostRegister}
            authLogin={this.authLogin}
            authResendOtp={this.authResendOtp}
            authForgetPassword={this.authForgetPassword}
            authForgetPasswordSms={this.authForgetPasswordSms}
            authResetTokenCheck={this.authResetTokenCheck}
            authResetPassword={this.authResetPassword}
            errors={this.state.errors}
            handleErrors={this.handleErrors}
          />
        )}
        exact={props.exact}
        strict={props.strict}
      />
    ));
  };

  render() {
    return <Authenticate getRoutes={this.getRoutes} {...this.props} />;
  }
}

AuthenticateBase.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
};

export default AuthenticateBase;
